import { Users, Clock, Globe, Target, Brain, Database, Code, Shield, Layout, Settings, Briefcase, TrendingUp, Handshake, Sparkles, ArrowRight, Star } from "lucide-react";

export default function RecruitmentServices() {
  const serviceCategories = [
    {
      id: "permanent",
      title: "Permanent Staffing",
      icon: Users,
      description: "Build your core engineering teams with permanent placements across all seniority levels, from junior developers to CTOs.",
      roles: [
        { name: "AI/ML Experts", icon: Brain },
        { name: "SAP Experts", icon: Database },
        { name: "ERP Experts", icon: Layout },
        { name: "Senior Engineers", icon: Code },
        { name: "Full-stack Developers", icon: Settings },
        { name: "DevOps & Infrastructure", icon: Shield }
      ],
      popular: true,
      bgColor: "#2c3e50",
      lightBg: "#f5f6f7"
    },
    {
      id: "contract",
      title: "Contract & Temporary",
      icon: Clock,
      description: "Access specialized talent for project-based needs, consulting, or team augmentation with flexible engagement.",
      roles: [
        { name: "Project-based contractors", icon: Briefcase },
        { name: "Team augmentation", icon: Users },
        { name: "Specialized consultants", icon: TrendingUp },
        { name: "Interim leadership", icon: Sparkles }
      ],
      popular: false,
      bgColor: "#2c3e50",
      lightBg: "#f5f6f7"
    },
    {
      id: "nearshoring",
      title: "Nearshoring Solutions",
      icon: Globe,
      description: "Build distributed teams across time zones with top talent from India, Eastern Europe, and Latin America.",
      roles: [
        { name: "Dedicated development", icon: Code },
        { name: "Cultural integration", icon: Handshake },
        { name: "Cost-optimized hiring", icon: TrendingUp },
        { name: "Managed team", icon: Users }
      ],
      popular: false,
      bgColor: "#2c3e50",
      lightBg: "#f5f6f7"
    },
    {
      id: "sourcing",
      title: "Direct Sourcing",
      icon: Target,
      description: "Access our proprietary database and passive candidate network for high-velocity, quality hiring at scale.",
      roles: [
        { name: "Passive candidate", icon: Target },
        { name: "Bulk hiring", icon: Users },
        { name: "Niche skill", icon: Brain },
        { name: "Market mapping", icon: Globe }
      ],
      popular: false,
      bgColor: "#2c3e50",
      lightBg: "#f5f6f7"
    }
  ];

  return (
    <section
      className="w-full py-20 md:py-28 lg:py-32"
      style={{ backgroundColor: "#ffffff" }}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        
        {/* Header Section */}
        <div className="text-center mb-16">
          {/* Bubble tag */}
          <div className="inline-flex items-center gap-2 mb-4">
            <div className="w-2 h-2 bg-black rounded-full"></div>
            <p 
              className="text-sm font-medium uppercase tracking-wider"
              style={{ 
                fontFamily: '"Open Sans", sans-serif',
                fontSize: '14px',
                fontWeight: 500,
                color: '#081120'
              }}
            >
              What We Offer
            </p>
          </div>

          <h2
            className="text-3xl md:text-4xl lg:text-5xl font-semibold leading-tight mb-5"
            style={{ 
              fontFamily: 'Outfit, sans-serif',
              fontWeight: 600,
              color: '#081120'
            }}
          >
            Recruitment Services
          </h2>

          <p
            className="text-lg max-w-2xl mx-auto text-center"
            style={{ 
              fontFamily: '"Open Sans", sans-serif',
              fontSize: '18px',
              lineHeight: '160%',
              color: '#555658'
            }}
          >
            Comprehensive solutions tailored to your hiring challenges
          </p>
        </div>

        {/* Service Cards */}
        <div className="grid lg:grid-cols-2 gap-8">
          {serviceCategories.map((service, idx) => {
            const Icon = service.icon;
            return (
              <div
                key={service.id}
                className="group relative"
              >
                {/* Main Card */}
                <div 
                  className="rounded-2xl p-8 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 h-full"
                  style={{ 
                    backgroundColor: service.lightBg,
                    border: `1px solid ${service.bgColor}20`,
                  }}
                >
                  {/* Popular Badge */}
                  {service.popular && (
                    <div className="absolute -top-3 left-8">
                      <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold" style={{ backgroundColor: service.bgColor, color: '#ffffff' }}>
                        <Star className="w-3 h-3 fill-current" />
                        Most Popular
                      </div>
                    </div>
                  )}

                  {/* Header */}
                  <div className="flex items-start justify-between mb-6">
                    <div 
                      className="w-14 h-14 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: `${service.bgColor}15` }}
                    >
                      <Icon className="w-7 h-7" style={{ color: service.bgColor }} />
                    </div>
                    <div className="flex gap-1.5">
                      <div className="w-1 h-1 rounded-full" style={{ backgroundColor: service.bgColor, opacity: 0.4 }}></div>
                      <div className="w-1 h-1 rounded-full" style={{ backgroundColor: service.bgColor, opacity: 0.4 }}></div>
                      <div className="w-1 h-1 rounded-full" style={{ backgroundColor: service.bgColor, opacity: 0.4 }}></div>
                    </div>
                  </div>

                  {/* Title */}
                  <h3
                    className="text-2xl font-semibold mb-3"
                    style={{ 
                      fontFamily: 'Outfit, sans-serif',
                      fontWeight: 600,
                      color: '#081120'
                    }}
                  >
                    {service.title}
                  </h3>
                  
                  {/* Description */}
                  <p
                    className="text-sm leading-relaxed mb-6"
                    style={{ 
                      fontFamily: '"Open Sans", sans-serif',
                      fontSize: '14px',
                      lineHeight: '165%',
                      color: '#555658',
                      hyphens:'none'
                    }}
                  >
                    {service.description}
                  </p>

                  {/* Roles Grid */}
                  <div className="flex flex-wrap gap-2.5 mb-7">
                    {service.roles.slice(0, 4).map((role, roleIdx) => {
                      const RoleIcon = role.icon;
                      return (
                        <div
                          key={roleIdx}
                          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs transition-all duration-300 hover:scale-105"
                          style={{ 
                            backgroundColor: '#ffffff',
                            border: `1px solid ${service.bgColor}20`,
                            color: '#081120'
                          }}
                        >
                          <RoleIcon className="w-3.5 h-3.5" style={{ color: service.bgColor }} />
                          <span className="font-medium">{role.name}</span>
                        </div>
                      );
                    })}
                    {service.roles.length > 4 && (
                      <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs" style={{ backgroundColor: '#ffffff', border: `1px solid ${service.bgColor}20`, color: '#555658' }}>
                        +{service.roles.length - 4} more
                      </span>
                    )}
                  </div>

                  {/* CTA Button */}
                  <a
                    href={`#${service.id}-staffing`}
                    className="inline-flex items-center gap-2 text-sm font-semibold transition-all duration-300 group/btn hover:gap-3"
                    style={{ color: service.bgColor }}
                  >
                    Explore {service.title.split(' ')[0]} Solutions
                    <ArrowRight className="w-4 h-4 transition-all duration-300 group-hover/btn:translate-x-1" style={{ color: service.bgColor }} />
                  </a>
                </div>

                {/* Decorative element */}
                <div 
                  className="absolute -bottom-3 -right-3 w-32 h-32 rounded-full -z-10 opacity-0 group-hover:opacity-10 transition-all duration-500"
                  style={{ backgroundColor: service.bgColor }}
                ></div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}